/**
 * API Client for BYK Admin
 *
 * Provides type-safe API calls to the backend.
 */

const API_BASE = "/api";

interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    credentials: "include",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || "An error occurred");
  }

  return data;
}

// =============================================================================
// Session API
// =============================================================================

export interface User {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: string;
}

export interface Session {
  user: User;
  session: {
    id: string;
    expiresAt: Date;
  };
}

export async function getSession(): Promise<Session | null> {
  try {
    // BetterAuth uses /auth/get-session not /auth/session
    const response = await apiFetch<{
      user: User;
      session: { id: string; expiresAt: string };
    }>("/auth/get-session");
    if (response?.session) {
      return {
        user: response.user,
        session: {
          id: response.session.id,
          expiresAt: new Date(response.session.expiresAt),
        },
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Initiate Google OAuth sign-in
 * BetterAuth uses POST /api/auth/sign-in/social with { provider: "google" }
 * Returns a redirect URL to Google's OAuth page
 */
export async function signInWithGoogle(): Promise<void> {
  const callbackURL = `${window.location.origin}/`;
  const response = await apiFetch<{ url: string }>("/auth/sign-in/social", {
    method: "POST",
    body: JSON.stringify({
      provider: "google",
      callbackURL,
    }),
  });

  // BetterAuth returns a URL to redirect to Google OAuth
  if (response?.url) {
    window.location.href = response.url;
  }
}

export async function signOut(): Promise<void> {
  await apiFetch("/auth/sign-out", { method: "POST" });
}

// =============================================================================
// Vendors API
// =============================================================================

export interface Vendor {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  website: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface VendorCreate {
  name: string;
  slug: string;
  logo?: string;
  website?: string;
  active?: boolean;
  sortOrder?: number;
}

export async function listVendors(params?: {
  active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Vendor>> {
  const searchParams = new URLSearchParams();
  if (params?.active !== undefined)
    searchParams.set("active", String(params.active));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  return apiFetch(`/vendors${query ? `?${query}` : ""}`);
}

export async function getVendor(id: string): Promise<ApiResponse<Vendor>> {
  return apiFetch(`/vendors/${id}`);
}

export async function createVendor(
  data: VendorCreate
): Promise<ApiResponse<Vendor>> {
  return apiFetch("/vendors", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateVendor(
  id: string,
  data: Partial<VendorCreate>
): Promise<ApiResponse<Vendor>> {
  return apiFetch(`/vendors/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteVendor(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/vendors/${id}`, { method: "DELETE" });
}

// =============================================================================
// Categories API
// =============================================================================

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: {
    products: number;
  };
}

export interface CategoryCreate {
  name: string;
  slug: string;
  icon?: string;
  active?: boolean;
  sortOrder?: number;
}

export async function listCategories(params?: {
  active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Category>> {
  const searchParams = new URLSearchParams();
  if (params?.active !== undefined)
    searchParams.set("active", String(params.active));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  return apiFetch(`/categories${query ? `?${query}` : ""}`);
}

export async function getCategory(id: string): Promise<ApiResponse<Category>> {
  return apiFetch(`/categories/${id}`);
}

export async function createCategory(
  data: CategoryCreate
): Promise<ApiResponse<Category>> {
  return apiFetch("/categories", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCategory(
  id: string,
  data: Partial<CategoryCreate>
): Promise<ApiResponse<Category>> {
  return apiFetch(`/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(
  id: string
): Promise<{ success: boolean }> {
  return apiFetch(`/categories/${id}`, { method: "DELETE" });
}

// =============================================================================
// Products API
// =============================================================================

export interface ProductDimensions {
  width: number;
  height: number;
  depth: number;
  unit: "cm" | "mm" | "m" | "in";
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  dimensions: ProductDimensions | null;
  glbKey: string | null;
  textureKey: string | null;
  thumbnailKey: string | null;
  price: number | null;
  currency: string;
  active: boolean;
  featured: boolean;
  vendorId: string;
  categoryId: string;
  vendor?: {
    id: string;
    name: string;
    slug: string;
  };
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProductCreate {
  sku: string;
  name: string;
  description?: string;
  dimensions?: ProductDimensions;
  glbKey?: string;
  textureKey?: string;
  thumbnailKey?: string;
  price?: number;
  currency?: string;
  active?: boolean;
  featured?: boolean;
  vendorId: string;
  categoryId: string;
}

export async function listProducts(params?: {
  vendorId?: string;
  categoryId?: string;
  active?: boolean;
  featured?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Product>> {
  const searchParams = new URLSearchParams();
  if (params?.vendorId) searchParams.set("vendorId", params.vendorId);
  if (params?.categoryId) searchParams.set("categoryId", params.categoryId);
  if (params?.active !== undefined)
    searchParams.set("active", String(params.active));
  if (params?.featured !== undefined)
    searchParams.set("featured", String(params.featured));
  if (params?.search) searchParams.set("search", params.search);
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  return apiFetch(`/products${query ? `?${query}` : ""}`);
}

export async function getProduct(id: string): Promise<ApiResponse<Product>> {
  return apiFetch(`/products/${id}`);
}

export async function createProduct(
  data: ProductCreate
): Promise<ApiResponse<Product>> {
  return apiFetch("/products", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProduct(
  id: string,
  data: Partial<Omit<ProductCreate, "sku">>
): Promise<ApiResponse<Product>> {
  return apiFetch(`/products/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/products/${id}`, { method: "DELETE" });
}

// =============================================================================
// Uploads API
// =============================================================================

export interface PresignResponse {
  token: string;
  key: string;
  expiresIn: number;
  maxSize: number;
}

export async function requestUpload(params: {
  filename: string;
  mimeType: string;
  entityType: "vendor" | "product" | "project" | "render" | "general";
  entityId?: string;
  fileSize?: number;
}): Promise<PresignResponse> {
  return apiFetch("/uploads/presign", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/**
 * Get MIME type from file, with fallback based on extension
 */
export function getMimeType(file: File): string {
  if (file.type) {
    return file.type;
  }

  // Fallback for files without MIME type (common for GLB/glTF)
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "glb":
      return "model/gltf-binary";
    case "gltf":
      return "model/gltf+json";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "pdf":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
}

export async function uploadFile(
  token: string,
  file: File
): Promise<ApiResponse<{ id: string; key: string; url: string }>> {
  const mimeType = getMimeType(file);

  const response = await fetch(`${API_BASE}/uploads/complete`, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType,
      "X-Upload-Token": token,
    },
    body: file,
    credentials: "include",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || "Upload failed");
  }

  return data;
}
